/**
 * EventBadge — Compact alert pill for a pick with recent corporate events.
 * Fetches events for a symbol and renders a color-coded dot + tooltip.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Module-level cache so hovering many rows doesn't hammer the API.
// Bump CACHE_VERSION to force a refresh when classifier logic changes.
const CACHE_VERSION = 'v3';
const _cache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

const fetchEvents = async (symbol, authFetch) => {
  const now = Date.now();
  const key = `${CACHE_VERSION}|${symbol}`;
  const cached = _cache.get(key);
  if (cached && now - cached.at < CACHE_TTL_MS) return cached.data;
  try {
    const r = await authFetch(`${API}/api/alphascout/events/${symbol}?days=7`);
    const d = await r.json();
    _cache.set(key, { at: now, data: d });
    return d;
  } catch (e) {
    return null;
  }
};

export const useSymbolEvents = (symbol) => {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    fetchEvents(symbol, authFetch).then((d) => { if (alive) setData(d); });
    return () => { alive = false; };
  }, [symbol, authFetch]);

  return useMemo(() => {
    if (!data?.events) return { red: 0, watch: 0, info: 0, events: [], worst: null };
    let red = 0, watch = 0, info = 0;
    for (const e of data.events) {
      if (e.severity === 'RED') red++;
      else if (e.severity === 'WATCH') watch++;
      else info++;
    }
    const worst = data.events.find((e) => e.severity === 'RED')
      || data.events.find((e) => e.severity === 'WATCH')
      || data.events[0];
    return { red, watch, info, events: data.events, worst };
  }, [data]);
};

const summarize = (e) => {
  if (!e) return '';
  const desc = (e.description || '').trim();
  const title = (e.title || '').trim();
  // Prefer description if it's meaningfully longer than the title
  if (desc && desc.length > title.length + 8) {
    // Trim to 160 chars at sentence boundary if possible
    const cut = desc.slice(0, 160);
    const dot = cut.lastIndexOf('.');
    return dot > 40 ? cut.slice(0, dot + 1) : cut + (desc.length > 160 ? '…' : '');
  }
  return title;
};

const EventBadge = ({ symbol, size = 'sm' }) => {
  const { red, watch, info, worst, events } = useSymbolEvents(symbol);

  if (!red && !watch && !info) return null;

  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';

  // RED or WATCH — full pill with count
  if (red || watch) {
    const isRed = red > 0;
    const Icon = isRed ? AlertCircle : AlertTriangle;
    const cls = isRed
      ? 'text-red-400 bg-red-500/10 border-red-500/40'
      : 'text-amber-400 bg-amber-500/10 border-amber-500/40';
    const tooltip = [
      isRed
        ? `${red} RED event${red > 1 ? 's' : ''} in last 7 days (blocks new picks)`
        : `${watch} WATCH event${watch > 1 ? 's' : ''} in last 7 days`,
      summarize(worst),
      worst?.event_date ? `Date: ${worst.event_date.slice(0, 10)}` : '',
    ].filter(Boolean).join('\n\n');
    return (
      <span
        className={`inline-flex items-center gap-0.5 px-1 py-0.5 border rounded-sm text-[9px] font-bold font-mono ${cls}`}
        title={tooltip}
        data-testid={`event-badge-${symbol}`}
      >
        <Icon className={iconSize} />
        {isRed ? red : watch}
      </span>
    );
  }

  // INFO only — subtle dot so user knows the scanner is tracking the symbol
  const latest = worst || events?.[0];
  const tooltip = [
    `${info} informational event${info > 1 ? 's' : ''} in last 7 days`,
    summarize(latest),
    latest?.event_date ? `Date: ${latest.event_date.slice(0, 10)}` : '',
  ].filter(Boolean).join('\n\n');
  return (
    <span
      className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-muted-foreground/50 hover:bg-muted-foreground transition-colors"
      title={tooltip}
      data-testid={`event-badge-${symbol}`}
    />
  );
};

export default EventBadge;
